import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Decode URL-safe Base64 string
 * Handles the URL-safe variant where + is replaced with - and / with _
 */
function decodeUrlSafeBase64(base64: string): string {
  // Convert URL-safe base64 to standard base64
  let standardBase64 = base64.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding if needed
  const padding = standardBase64.length % 4;
  if (padding > 0) {
    standardBase64 += '='.repeat(4 - padding);
  }

  // Decode base64 to string
  return atob(standardBase64);
}

/**
 * Parse a value - tries to parse as JSON, otherwise returns as string
 */
function parseValue(value: string): any {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

/**
 * Parse a query string format into an object
 * Handles: "id=989898-sfs87fs87f-sfsfs&extra_field=test"
 * Returns: { id: "989898-sfs87fs87f-sfsfs", extra_field: "test" }
 */
function parseQueryString(queryString: string): Record<string, any> {
  const result: Record<string, any> = {};

  if (!queryString) {
    return result;
  }

  // Split by & to get key=value pairs
  const pairs = queryString.split('&');

  for (const pair of pairs) {
    const [key, ...valueParts] = pair.split('=');
    // Join back in case value contains '='
    const value = valueParts.join('=');

    if (key) {
      // Decode URI components and parse value
      const decodedKey = decodeURIComponent(key.trim());
      const decodedValue = value ? decodeURIComponent(value.trim()) : '';
      result[decodedKey] = parseValue(decodedValue);
    }
  }

  return result;
}

export interface UseUnifiedDeepLinkOptions {
  /**
   * Whether to automatically clean the URL after extracting params
   * @default true
   */
  cleanUrl?: boolean;

  /**
   * Whether to log debug information in development mode
   * @default true
   */
  debug?: boolean;
}

export interface UseUnifiedDeepLinkResult<T = Record<string, any>> {
  /**
   * The merged state from URL params and location state
   */
  state: T;

  /**
   * Whether the hook has finished processing the URL
   */
  isReady: boolean;

  /**
   * Whether the state came from a deep link (had qfai or query params)
   */
  isFromDeepLink: boolean;
}

/**
 * Unified Deep Link Hook
 *
 * Handles deep link navigation by:
 * 1. Scanning for ?qfai= encoded parameter (Base64 JSON object)
 * 2. Scanning for other query parameters (id, mode, etc.)
 * 3. Merging with existing location.state
 * 4. Cleaning the URL by replacing with state
 *
 * @example
 * // URL: /reports/custom-report-designer?qfai=eyJpZCI6IjEyMyIsIm1vZGUiOiJlZGl0In0=
 * const { state, isReady } = useUnifiedDeepLink<{ id?: string; mode?: string }>();
 * // state = { id: "123", mode: "edit" }
 * // URL becomes: /reports/custom-report-designer (state stored in history.state)
 *
 * @example
 * // URL: /reports?id=123&status=active
 * const { state } = useUnifiedDeepLink();
 * // state = { id: "123", status: "active" }
 */
export function useUnifiedDeepLink<T extends Record<string, any> = Record<string, any>>(
  options: UseUnifiedDeepLinkOptions = {}
): UseUnifiedDeepLinkResult<T> {
  const { cleanUrl = true, debug = true } = options;

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [state, setState] = useState<T>(() => (location.state as T) || ({} as T));
  const [isReady, setIsReady] = useState(false);
  const [isFromDeepLink, setIsFromDeepLink] = useState(false);

  // Use ref to track if we've already processed this URL
  const processedRef = useRef(false);
  const lastSearchRef = useRef(location.search);

  useEffect(() => {
    // Reset processed flag if search params change
    if (lastSearchRef.current !== location.search) {
      processedRef.current = false;
      lastSearchRef.current = location.search;
    }

    // Skip if already processed or no search params
    if (processedRef.current) {
      setIsReady(true);
      return;
    }

    // Check if there are any query params to process
    const hasQueryParams = location.search.length > 0;

    if (!hasQueryParams) {
      // No query params, use existing location state
      setState((location.state as T) || ({} as T));
      setIsReady(true);
      processedRef.current = true;
      return;
    }

    // Mark as processing
    processedRef.current = true;

    try {
      // 1. Get qfai parameter (encoded JSON object)
      const wlaiParam = searchParams.get('qfai');

      let decodedWlai: Record<string, any> = {};
      if (wlaiParam) {
        try {
          // Check if the parameter has the 'wlai-' prefix and remove it
          let base64String = wlaiParam;
          if (wlaiParam.startsWith('wlai-')) {
            base64String = wlaiParam.substring(5); // Remove 'wlai-' prefix
            if (process.env.NODE_ENV === 'development' && debug) {
              console.log('[useUnifiedDeepLink] 🔧 Removed wlai- prefix:', {
                original: wlaiParam,
                cleaned: base64String,
              });
            }
          }

          // Decode Base64 to get the JSON string (e.g., '{"id": "3a1d366b-f08b-e3c8-fe02-1528bd6efcc6"}')
          const decodedString = decodeUrlSafeBase64(base64String);

          // Parse the JSON string into an object
          decodedWlai = JSON.parse(decodedString);

          if (process.env.NODE_ENV === 'development' && debug) {
            console.log('[useUnifiedDeepLink] 📥 Decoded qfai parameter:', {
              raw: wlaiParam,
              decodedString,
              decoded: decodedWlai,
            });
          }
        } catch (error) {
          console.warn('[useUnifiedDeepLink] Failed to decode qfai parameter:', error);
        }
      }

      // 2. Get all other query parameters (excluding qfai)
      const otherParams: Record<string, any> = {};
      searchParams.forEach((value, key) => {
        if (key !== 'qfai') {
          otherParams[key] = parseValue(value);
        }
      });

      if (process.env.NODE_ENV === 'development' && debug && Object.keys(otherParams).length > 0) {
        console.log('[useUnifiedDeepLink] 📋 Other query params:', otherParams);
      }

      // 3. Merge state: existing state < other params < decoded qfai (qfai has highest priority)
      const existingState = (location.state as Record<string, any>) || {};
      const mergedState = {
        ...existingState,
        ...otherParams,
        ...decodedWlai,
      } as T;

      if (process.env.NODE_ENV === 'development' && debug) {
        console.log('[useUnifiedDeepLink] 🔀 Merged state:', {
          existingState,
          otherParams,
          decodedWlai,
          merged: mergedState,
        });
      }

      // 4. Update state
      setState(mergedState);
      setIsFromDeepLink(true);

      // 5. Clean URL by navigating with state and removing query params
      if (cleanUrl) {
        const cleanPath = location.pathname;

        if (process.env.NODE_ENV === 'development' && debug) {
          console.log('[useUnifiedDeepLink] 🧹 Cleaning URL:', {
            before: `${location.pathname}${location.search}`,
            after: cleanPath,
          });
        }

        // Use navigate with replace to update state and clean URL
        navigate(cleanPath, {
          state: mergedState,
          replace: true,
        });
      }

      setIsReady(true);
    } catch (error) {
      console.error('[useUnifiedDeepLink] Error processing deep link:', error);
      setState((location.state as T) || ({} as T));
      setIsReady(true);
    }
  }, [location.search, location.pathname, location.state, searchParams, navigate, cleanUrl, debug]);

  return {
    state,
    isReady,
    isFromDeepLink,
  };
}

export default useUnifiedDeepLink;
